import { api } from "../../../services/api";
import type {
  CryptoOverviewItem,
  CryptoOverviewNetwork,
} from "../../../services/crypto";

export interface DepositNetwork {
  id: string;
  name: string;
  fullName: string;
  standard: string;
  logoUrl: string;
  currency: string;
  address: string;
  minAmount: string;
  platformFee: string;
}

/**
 * Fallback-сети — используются, пока идёт загрузка списка с API,
 * и если бэк недоступен. Полностью заменяются данными из `GET /networks`.
 */
export const DEPOSIT_NETWORKS: DepositNetwork[] = [
  {
    id: "TRC20",
    name: "Tron",
    fullName: "Tron (TRC20)",
    standard: "TRC20",
    logoUrl: "https://static.coincap.io/assets/icons/trx@2x.png",
    currency: "USDT",
    address: "TFdmTBaW55wNwfyd1NmMqcLBaW3ePAUodK",
    minAmount: "10 USDT",
    platformFee: "2 USDT",
  },
  {
    id: "BEP20",
    name: "BNB Smart Chain",
    fullName: "BNB Smart Chain (BEP20)",
    standard: "BEP20",
    logoUrl: "https://static.coincap.io/assets/icons/bnb@2x.png",
    currency: "USDT",
    address: "0xB1B2E3C4D5E6F7A8B9C0D1E2F3A4B5C6D7E8F9A0",
    minAmount: "5 USDT",
    platformFee: "1 USDT",
  },
];

interface BackendNetwork {
  code: string;
  name: string;
  currency_code: string;
  chain_id: string | null;
  deposit_address: string | null;
  memo: string | null;
  min_deposit: string | null;
  min_withdraw: string | null;
  explorer_url: string | null;
  icon: string | null;
  is_active: boolean;
  sort_order: number;
}

function displayName(n: BackendNetwork): string {
  const base = n.name || n.code;
  if (base.toLowerCase().includes(n.code.toLowerCase())) return base;
  return `${base} (${n.code})`;
}

/** Дефолтные иконки для сетей (fallback, если бэк не отдал URL). */
const NETWORK_ICON_MAP: Record<string, string> = {
  TRC20: "https://static.coincap.io/assets/icons/trx@2x.png",
  TRON: "https://static.coincap.io/assets/icons/trx@2x.png",
  BEP20: "https://static.coincap.io/assets/icons/bnb@2x.png",
  BSC: "https://static.coincap.io/assets/icons/bnb@2x.png",
  ERC20: "https://static.coincap.io/assets/icons/eth@2x.png",
  ETH: "https://static.coincap.io/assets/icons/eth@2x.png",
  BTC: "https://static.coincap.io/assets/icons/btc@2x.png",
  BITCOIN: "https://static.coincap.io/assets/icons/btc@2x.png",
  SOL: "https://static.coincap.io/assets/icons/sol@2x.png",
  SOLANA: "https://static.coincap.io/assets/icons/sol@2x.png",
};

function iconFor(n: BackendNetwork): string {
  if (n.icon) return n.icon;
  return NETWORK_ICON_MAP[n.code.toUpperCase()] ?? "";
}

/** Иконка сети по её коду (без bacend-объекта). */
export function iconForNetworkCode(code: string): string {
  return NETWORK_ICON_MAP[code.toUpperCase()] ?? "";
}

function mapNetwork(n: BackendNetwork): DepositNetwork {
  return {
    id: n.code,
    name: n.name,
    fullName: displayName(n),
    standard: n.code,
    logoUrl: iconFor(n),
    currency: n.currency_code,
    address: n.deposit_address ?? "—",
    minAmount: n.min_deposit
      ? `${n.min_deposit} ${n.currency_code}`
      : `— ${n.currency_code}`,
    platformFee:
      n.min_withdraw && Number(n.min_withdraw)
        ? `≈ ${n.min_withdraw} ${n.currency_code}`
        : `— ${n.currency_code}`,
  };
}

export async function fetchNetworks(): Promise<DepositNetwork[]> {
  const res = await api.get<BackendNetwork[]>("/networks");
  return res.data.map(mapNetwork);
}

/**
 * Ищем сеть в списке по её коду и валюте. `currency` опционален (для обратной
 * совместимости), но крайне рекомендуется — одна сеть теперь может содержать
 * несколько монет.
 */
export function getNetworkById(
  id: string | null,
  list: DepositNetwork[] = DEPOSIT_NETWORKS,
  currency?: string | null,
): DepositNetwork {
  if (!id) return list[0];
  const c = (currency ?? "").toUpperCase();
  if (c) {
    const exact = list.find(
      (n) => n.id === id && n.currency.toUpperCase() === c,
    );
    if (exact) return exact;
  }
  return list.find((n) => n.id === id) ?? list[0];
}

/**
 * Приводит сеть валюты (из `/me/crypto`) к формату `DepositNetwork`,
 * который использует `NetworkPickerDrawer`.
 *
 * Иконка сети:
 *   1) `n.icon` из БД (то, что администратор поставил через IconPicker);
 *   2) статический fallback-маппинг по коду (для сетей без иконки в БД).
 */
export function cryptoNetworkToDepositNetwork(
  item: Pick<CryptoOverviewItem, "code">,
  n: CryptoOverviewNetwork,
): DepositNetwork {
  const fullName =
    n.name && n.name.toLowerCase().includes(n.code.toLowerCase())
      ? n.name
      : `${n.name || n.code} (${n.code})`;
  return {
    id: n.code,
    name: n.name || n.code,
    fullName,
    standard: n.code,
    logoUrl: n.icon || iconForNetworkCode(n.code),
    currency: item.code,
    address: "—",
    minAmount: n.min_deposit
      ? `${n.min_deposit} ${item.code}`
      : `— ${item.code}`,
    platformFee: n.min_withdraw
      ? `≈ ${n.min_withdraw} ${item.code}`
      : `— ${item.code}`,
  };
}
